import { useSceneScale } from "@/landing/use-scene-scale.ts";

const BASE = import.meta.env.BASE_URL;
const SCENE_W = 1536;
const SCENE_H = 1024;

const REASONS = [
  {
    n: "04",
    id: "workbench",
    tone: "blue",
    label: "统一入口",
    title: "所有工具、数据与 AI，\n都在一个工作台",
    body: "在同一个工作入口，与同事和专家协作，访问有权限的项目资料，继续处理工作成果。",
    top: 154
  },
  {
    n: "03",
    id: "adapter",
    tone: "amber",
    label: "岗位交付",
    title: "员工熟悉业务，\n就能开始协作",
    body: "交付团队适配系统与流程。非技术人员用业务语言提出需求、确认成果，不必先学模型配置或 Skill 编写。",
    top: 382
  },
  {
    n: "02",
    id: "auto",
    tone: "violet",
    label: "团队积累",
    title: "讨论、资料与成果，\n让团队接着用",
    body: "团队 IM、会话历史、文件空间与知识库集中协作上下文。有权限的成员可以回看、引用和复用，不必从头交代。",
    top: 552
  },
  {
    n: "01",
    id: "local",
    tone: "blue",
    label: "私有部署",
    title: "从工作站到模型，\n按企业环境交付",
    body: "为 Agent、知识处理与文生图分别配置服务，结合硬件与推理参数验证。完整内网运行按部署清单验收。",
    top: 730
  }
] as const;

const MOBILE_REASONS = [...REASONS].reverse();

function plate(name: string) {
  return `${BASE}matterline/${name}.png`;
}

function MatterlineCopy({ reason }: { reason: (typeof REASONS)[number] }) {
  return (
    <div className="matterline-reason" data-tone={reason.tone}>
      <p className="matterline-reason-label">
        <span>{reason.n}</span>
        <i aria-hidden />
        {reason.label}
      </p>
      <h3>
        {reason.title.split("\n").map((line, index) => (
          <span key={line}>
            {line}
            {index === 0 ? <br /> : null}
          </span>
        ))}
      </h3>
      <p className="matterline-reason-body">{reason.body}</p>
    </div>
  );
}

function MatterlineFlow() {
  const paths = [
    "M 800 340 V 374",
    "M 598 490 V 522",
    "M 798 486 V 526",
    "M 1000 490 V 524",
    "M 536 654 V 702",
    "M 824 654 V 704",
    "M 1110 654 V 704"
  ];

  return (
    <svg className="matterline-flow" viewBox={`0 0 ${SCENE_W} ${SCENE_H}`} aria-hidden>
      <defs>
        <filter id="matterline-flow-glow" x="-100%" y="-40%" width="300%" height="180%">
          <feGaussianBlur stdDeviation="4" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      {paths.map((path, index) => (
        <g key={path} className="matterline-flow-link" data-tone={index < 1 ? "blue" : index < 4 ? "teal" : "violet"}>
          <path className="matterline-flow-line" d={path} />
          <path className="matterline-flow-pulse" d={path} />
        </g>
      ))}
    </svg>
  );
}

function MatterlineScene() {
  const { frameRef, scale } = useSceneScale(SCENE_W);

  return (
    <div className="matterline-scene-frame" ref={frameRef} style={{ height: SCENE_H * scale }}>
      <div className="matterline-scene" style={{ transform: `scale(${scale})` }}>
        <div className="matterline-grid" aria-hidden />
        <MatterlineFlow />

        <header className="matterline-head">
          <h2>Matterline，把企业智能长在企业内部。</h2>
          <p>
            从本地基础设施、企业知识到业务流程与员工入口，Matterline
            <br />
            不是一件外挂工具，而是一套为企业而生的私有智能工作台。
          </p>
        </header>

        <div className="matterline-reasons">
          {REASONS.map((reason) => (
            <div className="matterline-reason-slot" key={reason.n} style={{ top: reason.top }}>
              <MatterlineCopy reason={reason} />
              <span className="matterline-reason-connector" data-tone={reason.tone} aria-hidden />
            </div>
          ))}
        </div>

        {REASONS.map((reason) => (
          <figure className="matterline-layer" data-id={reason.id} key={reason.id}>
            <img src={plate(reason.id)} alt="" />
          </figure>
        ))}

        <p className="matterline-close">每一家企业，都应该拥有自己的智能。</p>
        <div className="matterline-finish" aria-hidden />
      </div>
    </div>
  );
}

function MatterlineMobile() {
  return (
    <div className="matterline-mobile">
      <header className="matterline-mobile-head">
        <h2>Matterline，把企业智能长在企业内部。</h2>
        <p>从本地基础设施到统一工作入口，一套为企业而生的私有智能工作台。</p>
      </header>

      <div className="matterline-mobile-stack">
        {MOBILE_REASONS.map((reason) => (
          <article className="matterline-mobile-reason" data-tone={reason.tone} key={reason.n}>
            <img src={plate(reason.id)} alt="" />
            <MatterlineCopy reason={reason} />
          </article>
        ))}
      </div>

      <p className="matterline-mobile-close">每一家企业，都应该拥有自己的智能。</p>
    </div>
  );
}

export function WhyMatterline() {
  return (
    <section className="why-matterline" id="why-matterline">
      <MatterlineScene />
      <MatterlineMobile />
    </section>
  );
}
